import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Bookmark, X, TrendingUp, TrendingDown, Minus, ArrowUpRight, Loader2 } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/datetime";

/**
 * InvestorWatchlist
 * -----------------
 * Institutions save companies they're quietly tracking. On page load we
 * compare current trust score vs the snapshot at add-time and highlight the
 * delta. Materially-moved companies (|delta| >= 5) fire notifications
 * server-side.
 */
function DeltaBadge({ delta }) {
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-mono rounded bg-emerald-500/12 text-emerald-700 border border-emerald-500/40" data-testid="watch-delta-up">
        <TrendingUp size={11} /> +{delta}
      </span>
    );
  }
  if (delta < 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-mono rounded bg-rose-500/12 text-rose-700 border border-rose-500/40" data-testid="watch-delta-down">
        <TrendingDown size={11} /> {delta}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-mono rounded bg-navy/[0.05] text-ink-soft border border-navy/10" data-testid="watch-delta-flat">
      <Minus size={11} /> 0
    </span>
  );
}

export default function InvestorWatchlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/investor/watchlist");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (startupId) => {
    try {
      await api.delete(`/investor/watchlist/${startupId}`);
      setItems((prev) => prev.filter((i) => i.startup_id !== startupId));
      toast.success("Removed from watchlist");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <DashboardShell title="Watchlist" subtitle="INVESTOR · QUIETLY TRACKING">
      <Toaster richColors position="top-right" />

      <div className="mb-6 text-sm text-ink-soft max-w-2xl">
        Companies you&apos;re quietly tracking. When a health score moves by 5 points or more, we&apos;ll drop a nudge into your bell.
      </div>

      {loading ? (
        <Card><div className="flex items-center gap-2 text-ink-muted text-sm"><Loader2 size={14} className="animate-spin" /> Loading watchlist…</div></Card>
      ) : items.length === 0 ? (
        <Card testId="watchlist-empty">
          <div className="flex items-start gap-3">
            <Bookmark size={18} className="text-[#8A6600] mt-0.5" />
            <div>
              <div className="font-heading text-ink text-lg mb-1">No companies watched yet</div>
              <div className="text-sm text-ink-muted">
                Tap the watch icon on any startup passport or marketplace card to add it here.
              </div>
              <Link
                to="/investor/marketplace"
                className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#8A6600] hover:underline"
                data-testid="watchlist-goto-marketplace"
              >
                Open the marketplace <ArrowUpRight size={12} />
              </Link>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4">
          {items.map((it) => (
            <div
              key={it.startup_id}
              className="glass rounded-lg p-5 flex flex-wrap items-center gap-4"
              data-testid={`watchlist-row-${it.startup_id}`}
            >
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/investor/startup-passport/${it.startup_id}`}
                    className="font-heading text-ink text-base hover:text-[#8A6600] transition-colors"
                    data-testid={`watchlist-link-${it.startup_id}`}
                  >
                    {it.startup_name || "Unnamed startup"}
                  </Link>
                  {it.sector ? <Pill tone="muted">{it.sector}</Pill> : null}
                  {it.business_stage ? <Pill tone="muted">{it.business_stage}</Pill> : null}
                </div>
                {it.note ? (
                  <div className="text-xs text-ink-muted mt-1 italic">&quot;{it.note}&quot;</div>
                ) : null}
                <div className="text-[11px] font-mono text-ink-muted mt-1.5">
                  Added {it.added_at ? formatDate(it.added_at) : "—"}
                </div>
              </div>
              <div className="text-center px-3 min-w-[120px]">
                <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Current</div>
                <div className="font-heading text-ink text-xl">
                  {(it.current_trust?.total ?? "—")}
                  <span className="text-ink-muted text-xs">/100</span>
                </div>
                <div className="mt-1"><DeltaBadge delta={it.delta || 0} /></div>
              </div>
              <button
                type="button"
                onClick={() => remove(it.startup_id)}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-rose-500/40 text-rose-700 hover:bg-rose-500/10 text-xs font-heading uppercase tracking-widest transition-colors"
                data-testid={`watchlist-remove-${it.startup_id}`}
              >
                <X size={13} /> Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
